import { RANKING, SIZING } from './constants';

/** Capped items step down by up to this fraction so the hottest post still looks hottest. */
const CAP_SPREAD = 0.1;

/**
 * Caps every weight at `maxShare` of the (new) total, keeping the others unchanged: water-filling
 * from the top. Items that hit the cap keep their rank order (each a little smaller than the one
 * above), so ties only happen when scores tie. Requires maxShare * n > 1, otherwise equal weights.
 */
export function capShares(weights: readonly number[], maxShare: number): number[] {
  const n = weights.length;
  if (n === 0) return [];
  if (maxShare * n <= 1) return weights.map(() => 1);
  const order = weights.map((_, i) => i).sort((a, b) => weights[b]! - weights[a]!);
  let rest = weights.reduce((a, b) => a + b, 0);
  for (let k = 0; k < n; k++) {
    const t = (maxShare * rest) / (1 - k * maxShare);
    const next = weights[order[k]!]!;
    if (next <= t) {
      const out = [...weights];
      for (let j = 0; j < k; j++) out[order[j]!] = t * (1 - (CAP_SPREAD * j) / k);
      return out;
    }
    rest -= next;
  }
  return weights.map(() => 1);
}

/**
 * Raises every weight to at least `minShare` of the (new) total: water-filling from the bottom.
 * Requires minShare * n < 1, otherwise returns equal weights.
 */
export function floorShares(weights: readonly number[], minShare: number): number[] {
  const n = weights.length;
  if (n === 0) return [];
  if (minShare <= 0) return [...weights];
  if (minShare * n >= 1) return weights.map(() => 1);
  const order = weights.map((_, i) => i).sort((a, b) => weights[a]! - weights[b]!);
  let rest = weights.reduce((a, b) => a + b, 0);
  for (let k = 0; k < n; k++) {
    const f = (minShare * rest) / (1 - k * minShare);
    const next = weights[order[k]!]!;
    if (next >= f) {
      const out = [...weights];
      for (let j = 0; j < k; j++) out[order[j]!] = f;
      return out;
    }
    rest -= next;
  }
  return weights.map(() => 1);
}

/** How many tiles fit on a board while every tile keeps a readable minimum size. */
export function maxTilesForArea(boardAreaPx: number): number {
  return Math.max(1, Math.floor(boardAreaPx / (SIZING.minTileAreaPx * 1.4)));
}

export interface SizingInput {
  id: string;
  score: number;
  isAgent?: boolean;
  /** New and not opened yet: gets one of the smallest tiles. */
  fresh?: boolean;
  /** When it was posted (ms): among new posts, the newer get a little more room. */
  createdAt?: number;
}

export interface SizedTile {
  id: string;
  /** Share of the board area; all shares sum to 1. */
  share: number;
}

/** The talk of the town: the post opened most (highest score, not fresh), or -1 when none is. */
export function talkOfTheTown(items: readonly SizingInput[]): number {
  let top = -1;
  items.forEach((it, i) => {
    if (!it.fresh && (top < 0 || it.score > items[top]!.score)) top = i;
  });
  return top;
}

/**
 * Turns scores into board shares: sublinear (score^0.6), AI summaries weighted down
 * (`SIZING.agentWeight`), new posts as small as the least-opened, no tile above the cap, and every
 * tile above the readable minimum. The talk of the town always gets the biggest tile, and at least
 * `SIZING.crownMinShare` of the board.
 */
export function tileShares(items: readonly SizingInput[], boardAreaPx: number): SizedTile[] {
  const n = items.length;
  if (n === 0) return [];

  // New posts weigh as little as the least opened, up to twice that for the newest, so a board of
  // nothing but new posts still has a lead (the newest) instead of a grid of equal cards.
  const freshWeight = RANKING.minScore ** SIZING.exponent * SIZING.agentWeight;
  const times = items.filter((it) => it.fresh).map((it) => it.createdAt ?? 0);
  const oldest = Math.min(...times);
  const span = Math.max(...times) - oldest;
  let w = items.map((it) =>
    it.fresh
      ? freshWeight * (1 + (span > 0 ? ((it.createdAt ?? 0) - oldest) / span : 0))
      : Math.max(it.score, 1e-6) ** SIZING.exponent * (it.isAgent ? SIZING.agentWeight : 1),
  );
  const top = talkOfTheTown(items);
  if (top >= 0) {
    const others = Math.max(0, ...w.filter((_, i) => i !== top));
    if (w[top]! <= others) w[top] = others * 1.15;
  }
  w = capShares(w, Math.max(SIZING.maxShare, 1.5 / n));
  // The smallest tile still gets a fair slice of a board with few posts (a third of an equal
  // share), so new posts look small, not like slivers; with many posts the readable minimum rules.
  const readable = boardAreaPx > 0 ? SIZING.minTileAreaPx / boardAreaPx : 0;
  const minShare = Math.min(Math.max(readable, 0.35 / n), 0.9 / n);
  w = floorShares(w, minShare);

  const total = w.reduce((a, b) => a + b, 0);
  let shares = w.map((x) => x / total);
  // The talk of the town is never small: it takes at least crownMinShare, from the others alike.
  if (top >= 0 && shares[top]! < SIZING.crownMinShare) {
    const rest = (1 - SIZING.crownMinShare) / (1 - shares[top]!);
    shares = shares.map((s, i) => (i === top ? SIZING.crownMinShare : s * rest));
  }
  return items.map((it, i) => ({ id: it.id, share: shares[i]! }));
}
